/**
 * Locale model (docs/architecture/internationalization.md §2, planning-app i18n section PA25+):
 * the UI locale is per person (`people.locale`), falls back along `Accept-Language`, then `en`.
 * v1 ships English (source), French and — since PA40, on the same footing — Spanish. `en-XA` is
 * the pseudo-locale CI builds to catch hard-coded or untranslated strings; it is never offered to people.
 */
export const SUPPORTED_LOCALES = ['en', 'fr', 'es'] as const;
/** The locales translated from English: every rule that holds for one holds for the others (PA40). */
export const TRANSLATED_LOCALES = ['fr', 'es'] as const satisfies readonly Locale[];
export type TranslatedLocale = (typeof TRANSLATED_LOCALES)[number];
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';
export const PSEUDO_LOCALE = 'en-XA';
/** Non-HttpOnly cookie the SPA writes so server-rendered pages (refusal, errors) match the UI locale. */
export const LOCALE_COOKIE = 'brain_locale';

export const LOCALE_NAMES: Record<Locale, string> = { en: 'English', fr: 'Français', es: 'Español' };

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function isTranslatedLocale(value: unknown): value is TranslatedLocale {
  return typeof value === 'string' && (TRANSLATED_LOCALES as readonly string[]).includes(value);
}

/**
 * The locale-specific typesetting applied to a translated catalog and to dynamic text: French gets
 * the narrow no-break spaces and « » (PA26); Spanish punctuation is the English one, so nothing.
 */
export function typesetFor(locale: Locale): (text: string) => string {
  return locale === 'fr' ? frenchTypography : (text) => text;
}

/**
 * RFC 7231 Accept-Language negotiation against the supported locales: q-values honoured, a
 * region tag (`fr-CA`) matches its base language, unknown languages are skipped, `en` otherwise.
 */
export function negotiateLocale(acceptLanguage: string | null | undefined, fallback: Locale = DEFAULT_LOCALE): Locale {
  if (!acceptLanguage) return fallback;
  const ranked = acceptLanguage
    .split(',')
    .map((part, index) => {
      const [tag = '', ...params] = part.trim().split(';');
      const q = params.map((p) => p.trim()).find((p) => p.startsWith('q='));
      const weight = q ? Number(q.slice(2)) : 1;
      return { tag: tag.trim().toLowerCase(), weight: Number.isFinite(weight) ? weight : 0, index };
    })
    .filter((r) => r.tag && r.weight > 0)
    .sort((a, b) => b.weight - a.weight || a.index - b.index);
  for (const { tag } of ranked) {
    if (tag === '*') return fallback;
    const base = tag.split('-')[0] ?? tag;
    if (isLocale(base)) return base;
  }
  return fallback;
}

/** Picks the first supported locale from an ordered list such as `navigator.languages`. */
export function pickLocale(candidates: readonly (string | null | undefined)[], fallback: Locale = DEFAULT_LOCALE): Locale {
  for (const c of candidates) {
    if (!c) continue;
    const base = c.toLowerCase().split('-')[0] ?? '';
    if (isLocale(base)) return base;
  }
  return fallback;
}

const NBSP = '\u00A0';
const NNBSP = '\u202F';

/**
 * French typography (PA26, planning-app.md §3.7): a narrow no-break space (U+202F) before `:`,
 * `;`, `?`, `!` and inside « »; straight quotes become « ». The Imprimerie nationale would use a
 * full no-break space before the colon — the spec chose the narrow one everywhere, which is also
 * what recent French style guides and keyboards do. Applied to the French catalog at compile time
 * and available for dynamic text. Leaves URLs, times (`12:30`) and ICU arguments alone; an
 * existing no-break space (either width) is kept as is.
 */
export function frenchTypography(text: string): string {
  let out = '';
  let depth = 0; // inside ICU {…} nothing is touched
  let quoteOpen = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i] as string;
    if (ch === '{') depth++;
    if (ch === '}') depth = Math.max(0, depth - 1);
    if (depth > 0 || (ch === '{' && depth === 1)) {
      out += ch;
      continue;
    }
    if (ch === '"') {
      // Avoid touching inch marks / code; only pair quotes around words.
      if (!quoteOpen) {
        out += `«${NNBSP}`;
        quoteOpen = true;
      } else {
        out += `${NNBSP}»`;
        quoteOpen = false;
      }
      continue;
    }
    if (ch === ':' || ch === ';' || ch === '?' || ch === '!') {
      const prev = out[out.length - 1];
      const next = text[i + 1];
      const isTime = ch === ':' && /\d/.test(prev ?? '') && /\d/.test(next ?? '');
      const isUrl = ch === ':' && next === '/';
      const isEmoticonOrDouble = prev === ch || prev === NBSP || prev === NNBSP;
      if (!isTime && !isUrl && !isEmoticonOrDouble && prev !== undefined && prev !== ' ' && prev !== '\n') {
        out += NNBSP + ch;
        continue;
      }
      if (prev === ' ') {
        out = out.slice(0, -1) + NNBSP + ch;
        continue;
      }
    }
    out += ch;
  }
  return out;
}
